import React, {useContext} from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import {CircularProgress, FormControl} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {Controller, useForm} from "react-hook-form";
import {IState} from "../../queries/pharmacy/pharmacyModel";
import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {physicalAddressValidationSchema} from "../../constants/validationSchemas";
import {useMutationUpdatePharmacy} from "../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import {NotificationType} from "../../constants/global";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";


interface PhysicalAddressProps {
  data: any;
  states: IState[];
  pharmacyId: number;
  isAdminEdit?: boolean;
  refetchPharmacy?: () => void;
}

const PhysicalAddress:React.FunctionComponent<PhysicalAddressProps> = ({
  data,
  states,
  pharmacyId,
  isAdminEdit,
  refetchPharmacy
  }) => {
  const {addToast} = useContext(ToasterContext);
  const { register, handleSubmit, formState: { errors }, control } = useForm({
    resolver: yupResolver(physicalAddressValidationSchema)
  });
  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy();

  const handlePhysicalAddressSubmit = async (values: any) => {
    const requestData = {
      PharmacyAddress: {
        update: {
          data: {
            address: { set: values.physicalAddress },
            city: { set: values.city },
            zipCode: { set: values.zipCode },
            State: {
              connect: {
                id: +values.state
              }
            },
          },
          where: { id: data[0].id }
        }
      },
    }

    updatePharmacy({ variables: { data: requestData, where: { id: pharmacyId } } }).then(() => {
      addToast({ type: NotificationType.Success, title: 'Your changes has been successfully saved'  });
      refetchPharmacy && refetchPharmacy();
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  }

  return (
    <Box component="form" onSubmit={handleSubmit(handlePhysicalAddressSubmit)} noValidate>
      { !isAdminEdit && <Typography component="h1" variant="h5" sx={{mb: 3}}>Physical Address</Typography> }
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={12}>
          <TextField
            label="Physical Address"
            id="physicalAddress"
            fullWidth
            required
            defaultValue={data[0].address}
            helperText={errors.physicalAddress?.message?.toString()}
            {...register('physicalAddress')}
            error={!!errors.physicalAddress}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="City"
            id="city"
            fullWidth
            required
            defaultValue={data[0].city}
            helperText={errors.city?.message?.toString()}
            {...register('city')}
            error={!!errors.city}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="state"
            control={control}
            defaultValue={data[0].State.id}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="state-label">State</InputLabel>
                <Select
                  {...field}
                  labelId="state-label"
                  label="State"
                  id="state"
                  fullWidth
                  required
                  {...register('state')}
                  error={!!errors.state}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                >
                  {states.map((state: IState) => (
                    <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
        <TextField
          autoComplete="zipCode"
          label="Zip Code"
          id="zipCode"
          fullWidth
          required
          defaultValue={data[0].zipCode}
          helperText={errors.zipCode?.message?.toString()}
          {...register('zipCode')}
          error={!!errors.zipCode}
        />
      </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', pt: 2 }}>
        <Button type="submit" variant='contained' sx={{width: '200px'}}>
          Save
          { loading && <CircularProgress sx={{ml: 2}} size={14} color='inherit' />}
        </Button>
      </Box>
    </Box>
  )
}

export default PhysicalAddress;